import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import HeaderBar from '../components/HeaderBar';
import { patientsService, Patient } from '../services/patientsService';

const PatientsList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'name' | 'alert'>('name');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        const patientsData = await patientsService.getPatients();
        console.log('API Patients Response:', patientsData);
        setPatients(patientsData);
      } catch (error) {
        console.error('Failed to fetch patients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, []);

  // Helper function to get full name
  const getFullName = (patient: Patient) => {
    return `${patient.firstName} ${patient.lastName}`.trim() || patient.username;
  };

  // Helper function to determine if patient has alert (you can customize this logic)
  const hasAlert = (patient: Patient) => {
    // For now, let's say patients without firstName/lastName have alerts
    return !patient.firstName || !patient.lastName;
  };

  const filtered = patients
    .filter((patient: Patient) => {
      const fullName = getFullName(patient);
      return fullName.toLowerCase().includes(search.toLowerCase()) ||
             patient.email.toLowerCase().includes(search.toLowerCase()) ||
             patient.username.toLowerCase().includes(search.toLowerCase());
    })
    .sort((a: Patient, b: Patient) => {
      if (sort === 'alert') {
        return (hasAlert(b) ? 1 : 0) - (hasAlert(a) ? 1 : 0);
      }
      return getFullName(a).localeCompare(getFullName(b));
    });

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
      <HeaderBar title="Mis pacientes" rightContent={<button style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <label style={{ fontWeight: 500, marginRight: 8 }}>ordenar por:</label>
            <select
              value={sort}
              onChange={e => setSort(e.target.value as 'name' | 'alert')}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                fontSize: 16,
                background: '#fff',
                fontWeight: 500,
                color: '#222',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            >
              <option value="name">nombre</option>
              <option value="alert">con alerta</option>
            </select>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search by name"
            />
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            Loading patients...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            No patients found
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    PACIENTE
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    EMAIL
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    TELÉFONO
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    ESTADO
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    ACCIONES
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((patient: Patient, index: number) => (
                  <tr 
                    key={patient.id}
                    style={{ 
                      borderBottom: index < filtered.length - 1 ? '1px solid #f1f5f9' : 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                    onClick={() => navigate(`/app/patient/${patient.id}`)}
                  >
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: patient.avatarUrl ? `url(${patient.avatarUrl})` : '#38b2ac',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: 16, 
                          fontWeight: 600, 
                          color: '#fff',
                          flexShrink: 0
                        }}>
                          {!patient.avatarUrl && getFullName(patient).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 16 }}>
                            {getFullName(patient)}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: 14 }}>
                            @{patient.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                      {patient.email}
                    </td>
                    <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                      {patient.phone_number || '-'}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                      {hasAlert(patient) ? (
                        <span style={{ 
                          background: '#fee2e2', 
                          color: '#dc2626', 
                          padding: '4px 12px', 
                          borderRadius: 16, 
                          fontSize: 12, 
                          fontWeight: 600 
                        }}>
                          ⚠️ Alerta
                        </span>
                      ) : (
                        <span style={{ 
                          background: '#dcfce7', 
                          color: '#16a34a', 
                          padding: '4px 12px', 
                          borderRadius: 16, 
                          fontSize: 12, 
                          fontWeight: 600 
                        }}>
                          ✓ Normal
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/patient/${patient.id}`);
                        }}
                        style={{
                          background: '#38b2ac',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 16px',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#319795';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#38b2ac';
                        }}
                      >
                        Ver perfil
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientsList; 